import axios from 'axios';
import { axiosClient } from '@/shared/api/axiosClient';
import { env } from '@/shared/config/env';
import type { ApiSuccess } from '@/shared/api/apiResponse.types';
import type { DjEvent, EventCounts } from '../model/event.types';
import type { SpotifyTrack } from '@/entities/spotify/model/spotify.types';

// Bare client for unauthenticated public calls — skips the global error
// interceptor so pages can handle 404s inline without a duplicate toast.
const publicClient = axios.create({ baseURL: env.apiBaseUrl, timeout: 15000 });

export type DjStats = {
  activeEvents: number;
  pendingRequests: number;
  guestsTunedIn: number;
};

export type PublicEvent = {
  eventId: string;
  eventCode: string;
  name: string;
  venueName?: string;
  eventDate?: string;
  startTime?: string;
  endTime?: string;
  recurrence?: string;
  recurrenceDays?: number[];
  recurrenceEndDate?: string;
  status: 'PENDING' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
  requestStatus: 'OPEN' | 'PAUSED' | 'CLOSED';
  isPlaying?: boolean;
  currentlyPlayingTrackId?: string;
  sharePlaylist?: boolean;
};

export const eventApi = {
  getPublicEvent: (eventCode: string) =>
    publicClient
      .get<ApiSuccess<PublicEvent>>(`/events/${eventCode}/public`)
      .then((r) => r.data.data),

  getPublicEventPlayback: (eventCode: string) =>
    publicClient
      .get<ApiSuccess<{ isPlaying: boolean; currentlyPlayingTrackId?: string }>>(`/events/${eventCode}/playback`)
      .then((r) => r.data.data),

  getPublicPlaylist: (eventCode: string) =>
    publicClient
      .get<ApiSuccess<SpotifyTrack[]>>(`/events/${eventCode}/playlist`)
      .then((r) => r.data.data),

  getDjStats: () =>
    axiosClient
      .get<ApiSuccess<DjStats>>('/dj/stats')
      .then((r) => r.data.data),

  listDjEvents: (status?: 'CLOSED' | 'ARCHIVED') =>
    axiosClient
      .get<ApiSuccess<DjEvent[]>>('/dj/events', { params: status ? { status } : undefined })
      .then((r) => r.data.data),

  getDjEventCounts: () =>
    axiosClient
      .get<ApiSuccess<EventCounts>>('/dj/events/counts')
      .then((r) => r.data.data),

  getEvent: (eventId: string) =>
    axiosClient
      .get<ApiSuccess<DjEvent>>(`/dj/events/${eventId}`)
      .then((r) => r.data.data),

  createEvent: (payload: Partial<DjEvent>) =>
    axiosClient
      .post<ApiSuccess<DjEvent>>('/dj/events', payload)
      .then((r) => r.data.data),

  updateEvent: (eventId: string, payload: Partial<DjEvent>) =>
    axiosClient
      .patch<ApiSuccess<DjEvent>>(`/dj/events/${eventId}`, payload)
      .then((r) => r.data.data),

  getEventPlayback: (eventId: string) =>
    axiosClient
      .get<ApiSuccess<{ isPlaying: boolean }>>(`/dj/events/${eventId}/playback`)
      .then((r) => r.data.data),

  deleteEvent: (eventId: string) =>
    axiosClient.delete(`/dj/events/${eventId}`),
};
